import { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Upload, X, FileText, Loader2, CheckCircle, AlertCircle, Sparkles, ScanLine, Check, Cloud, RefreshCw, Link, Clock, Copy } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { showAIErrorToast } from "@/lib/aiErrorHandler";
import { cn } from "@/lib/utils";
import { useUsageLimits } from "@/hooks/useUsageLimits";
import { useGmailConnection } from "@/hooks/useGmailConnection";
import { useDropboxConnection } from "@/hooks/useDropboxConnection";
import { GmailSyncProgress } from "@/components/gmail/GmailSyncProgress";
import { usePdfThumbnailGenerator } from "@/hooks/usePdfThumbnailGenerator";

// Format relative time in French
const formatRelativeTime = (dateStr: string | null): string => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "À l'instant";
  if (diffMins < 60) return `Il y a ${diffMins} min`;
  if (diffHours < 24) return `Il y a ${diffHours}h`;
  if (diffDays === 1) return "Hier";
  if (diffDays < 7) return `Il y a ${diffDays} jours`;
  return date.toLocaleDateString("fr-FR", { day: "numeric", month: "short" });
};

const typeLabels: Record<string, string> = {
  invoice: "Facture",
  contract: "Contrat",
  tax: "Fiscalité",
  insurance: "Assurance",
  identity: "Identité",
  receipt: "Reçu",
  quote: "Devis",
  payslip: "Bulletin de salaire",
  attestation: "Attestation",
  bank_statement: "Relevé bancaire",
  medical_certificate: "Certificat médical",
  notarial_deed: "Acte notarié",
  administrative: "Document administratif",
  warranty: "Garantie",
  other: "Autre",
};

type UploadStatus = 
  | "pending" 
  | "uploading" 
  | "validating"
  | "processing" 
  | "success" 
  | "error" 
  | "duplicate";

interface UploadFile {
  file: File;
  id: string;
  status: UploadStatus;
  progress: number;
  error?: string;
  detectedType?: string;
  documentId?: string;
}

interface DocumentUploadProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUploadComplete?: () => void;
}

// SVG Icons pour les marques officielles
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const OutlookIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#0078D4" d="M24 7.387v10.478c0 .23-.08.424-.238.576-.158.152-.356.228-.593.228h-8.326V6.583h8.326c.237 0 .435.076.593.228.158.152.238.346.238.576z" />
    <path fill="#28A8EA" d="M14.843 6.583v11.086h-5.95V6.583h5.95z" />
    <path fill="#0078D4" d="M14.843 18.669H8.893v-12.086h5.95z" />
    <path fill="#50D9FF" d="M8.893 6.583v12.086H2.831c-.237 0-.435-.076-.593-.228C2.08 18.29 2 18.095 2 17.865V7.387c0-.23.08-.424.238-.576.158-.152.356-.228.593-.228h6.062z" />
    <ellipse fill="#0078D4" cx="5.5" cy="12.5" rx="3" ry="3.5" />
    <ellipse fill="#fff" cx="5.5" cy="12.5" rx="2" ry="2.5" />
  </svg>
);

const GoogleDriveIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.5 15.75L15.75 4.5H8.25L15 15.75H22.5Z" />
    <path fill="#FBBC05" d="M8.25 4.5L1.5 15.75L4.875 21.75H12L8.25 4.5Z" />
    <path fill="#34A853" d="M15 15.75H4.875L8.25 21.75H18.375L22.5 15.75H15Z" />
  </svg>
);

const DropboxIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#0061FF" d="M12 6.17L6.23 9.77L12 13.37L17.77 9.77L12 6.17Z" />
    <path fill="#0061FF" d="M6.23 2L0.46 5.6L6.23 9.2L12 5.6L6.23 2Z" />
    <path fill="#0061FF" d="M17.77 2L12 5.6L17.77 9.2L23.54 5.6L17.77 2Z" />
    <path fill="#0061FF" d="M0.46 12.8L6.23 16.4L12 12.8L6.23 9.2L0.46 12.8Z" />
    <path fill="#0061FF" d="M17.77 16.4L23.54 12.8L17.77 9.2L12 12.8L17.77 16.4Z" />
    <path fill="#0061FF" d="M12 14.37L6.23 17.97L7.14 18.54V19.2L12 22L16.86 19.2V18.54L17.77 17.97L12 14.37Z" />
  </svg>
);

const WhatsAppIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#25D366" d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const SOURCE_OPTIONS = [
  { 
    id: "gmail", 
    name: "Gmail", 
    icon: GoogleIcon, 
    bgColor: "bg-white border border-gray-100",
    description: "Importez vos pièces jointes"
  },
  { 
    id: "dropbox", 
    name: "Dropbox", 
    icon: DropboxIcon, 
    bgColor: "bg-white border border-gray-100",
    description: "Synchronisez vos fichiers"
  },
  { 
    id: "outlook", 
    name: "Outlook", 
    icon: OutlookIcon, 
    bgColor: "bg-white border border-gray-100",
    description: "Bientôt disponible",
    disabled: true
  },
  { 
    id: "drive", 
    name: "Google Drive", 
    icon: GoogleDriveIcon, 
    bgColor: "bg-white border border-gray-100",
    description: "Bientôt disponible",
    disabled: true
  },
  { 
    id: "whatsapp", 
    name: "WhatsApp", 
    icon: WhatsAppIcon, 
    bgColor: "bg-white border border-gray-100",
    description: "Bientôt disponible",
    disabled: true
  },
];

export function DocumentUpload({ open, onOpenChange, onUploadComplete }: DocumentUploadProps) {
  const [files, setFiles] = useState<UploadFile[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState("files");
  
  const { checkStorageLimit, refreshUsage } = useUsageLimits();
  const gmail = useGmailConnection();
  const dropbox = useDropboxConnection();
  const { generatePdfThumbnail } = usePdfThumbnailGenerator();

  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

  // Refresh usage when dialog opens
  useEffect(() => {
    if (open) {
      refreshUsage();
    }
  }, [open, refreshUsage]);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: any[]) => {
    // Check storage limit before accepting files
    const storageCheck = checkStorageLimit();
    if (!storageCheck.allowed) {
      toast.error("Limite de documents atteinte", {
        description: storageCheck.reason,
      });
      return;
    }

    // Handle rejected files
    if (rejectedFiles.length > 0) {
      const errors = rejectedFiles.map(r => {
        if (r.errors?.[0]?.code === 'file-too-large') {
          return `${r.file.name}: Fichier trop volumineux (max 10 Mo)`;
        }
        if (r.errors?.[0]?.code === 'file-invalid-type') {
          return `${r.file.name}: Type de fichier non supporté`;
        }
        return `${r.file.name}: Fichier invalide`;
      });
      toast.error("Fichiers refusés", {
        description: errors.join("\n"),
      });
    }

    // Check for oversized files in accepted (shouldn't happen but double-check)
    const oversizedFiles = acceptedFiles.filter(f => f.size > MAX_FILE_SIZE);
    const validFiles = acceptedFiles.filter(f => f.size <= MAX_FILE_SIZE);
    
    if (oversizedFiles.length > 0) {
      toast.error(`Fichier trop volumineux. Maximum 10 Mo autorisé.`, {
        description: oversizedFiles.map(f => f.name).join(", ")
      });
    }

    const newFiles: UploadFile[] = validFiles.map((file) => ({
      file,
      id: `${file.name}-${Date.now()}-${Math.random()}`,
      status: "pending",
      progress: 0,
    }));
    setFiles((prev) => [...prev, ...newFiles]);
  }, [checkStorageLimit]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg", ".webp"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    },
    maxSize: MAX_FILE_SIZE,
  });

  const removeFile = (id: string) => {
    setFiles((prev) => prev.filter((f) => f.id !== id));
  };

  const handleSourceConnect = async (sourceId: string) => {
    if (sourceId === "gmail") {
      await gmail.connect();
    } else if (sourceId === "dropbox") {
      await dropbox.connect();
    } else {
      toast.info("Cette source sera bientôt disponible");
    }
  };

  // Single unified import function - upload, validate, and process in one go
  const importFiles = async () => {
    const pendingFiles = files.filter(f => f.status === "pending");
    if (pendingFiles.length === 0) return;

    setIsUploading(true);

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error("Vous devez être connecté pour importer des documents");
      setIsUploading(false);
      return;
    }

    let successCount = 0;
    let duplicateCount = 0;
    let errorCount = 0;

    // Process all files in parallel for better performance
    const uploadPromises = pendingFiles.map(async (uploadFile) => {
      let filePath: string | null = null;
      
      try {
        // Step 1: Upload to storage
        setFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id ? { ...f, status: "uploading", progress: 20 } : f
          )
        );

        const fileExt = uploadFile.file.name.split(".").pop()?.toLowerCase() || "pdf";
        filePath = `${user.id}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

        const { error: storageError } = await supabase.storage
          .from("documents")
          .upload(filePath, uploadFile.file);

        if (storageError) {
          console.error("[Upload] Storage error:", storageError);
          throw new Error(storageError.message || "Erreur lors du téléchargement");
        }

        // Step 2: Validate with firewall
        setFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id ? { ...f, status: "validating", progress: 50 } : f
          )
        );

        const { data: validateResult, error: validateError } = await supabase.functions.invoke(
          "validate-document",
          {
            body: {
              fileName: uploadFile.file.name,
              fileSize: uploadFile.file.size,
              mimeType: uploadFile.file.type,
              filePath,
              source: "upload",
              userId: user.id,
              title: uploadFile.file.name.replace(/\.[^/.]+$/, ""),
            },
          }
        );

        if (validateError) {
          console.error("[Upload] Validate error:", validateError);
          throw new Error(validateError.message || "Erreur de validation");
        }

        // Handle rejection (duplicate, invalid, etc.)
        if (!validateResult?.success) {
          const reason = validateResult?.rejectionReason || "Document refusé";
          
          // Clean up uploaded file
          await supabase.storage.from("documents").remove([filePath]);
          
          // Check if duplicate
          if (reason.toLowerCase().includes("doublon") || reason.toLowerCase().includes("duplicate")) {
            setFiles((prev) =>
              prev.map((f) =>
                f.id === uploadFile.id
                  ? { ...f, status: "duplicate", error: "Ce document existe déjà" }
                  : f
              )
            );
            duplicateCount++;
            return;
          }
          
          throw new Error(reason);
        }

        const documentId = validateResult.documentId;

        // Step 3: Processing (classification happens async via queue)
        setFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id ? { ...f, status: "processing", progress: 75, documentId } : f
          )
        );

        // Fire-and-forget: document actions + thumbnail only
        // NOTE: Classification is now handled exclusively by process-document-queue (Single Writer Pattern)
        if (documentId) {
          // Generate document actions (writes to document_actions table only)
          supabase.functions.invoke('generate-document-actions', {
            body: { document_id: documentId }
          }).catch(err => console.warn('[Upload] Actions generation error:', err));

          // Generate thumbnail for images (server-side)
          if (uploadFile.file.type.startsWith('image/')) {
            supabase.functions.invoke('generate-thumbnail', {
              body: { 
                documentId,
                filePath,
                mimeType: uploadFile.file.type
              }
            }).catch(err => console.warn('[Upload] Thumbnail generation error:', err));
          }
          
          // Generate thumbnail for PDFs (client-side using react-pdf)
          if (uploadFile.file.type === 'application/pdf') {
            generatePdfThumbnail(uploadFile.file, documentId, user.id)
              .catch(err => console.warn('[Upload] PDF thumbnail generation error:', err));
          }
        }

        // Success!
        setFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id 
              ? { ...f, status: "success", progress: 100, documentId } 
              : f
          )
        );
        successCount++;

      } catch (error: any) {
        console.error("[Upload] Error for", uploadFile.file.name, error);
        
        // Clean up uploaded file on error
        if (filePath) {
          try {
            await supabase.storage.from("documents").remove([filePath]);
          } catch (cleanupErr) {
            console.warn("[Upload] Failed to cleanup file:", cleanupErr);
          }
        }
        
        setFiles((prev) =>
          prev.map((f) =>
            f.id === uploadFile.id
              ? { ...f, status: "error", error: error.message || "Erreur lors de l'import" }
              : f
          )
        );
        errorCount++;
      }
    });

    // Wait for all uploads to complete
    await Promise.all(uploadPromises);

    setIsUploading(false);

    // Show summary toast
    if (successCount > 0) {
      toast.success(`${successCount} document${successCount > 1 ? 's' : ''} importé${successCount > 1 ? 's' : ''}`, {
        description: duplicateCount > 0 
          ? `${duplicateCount} doublon${duplicateCount > 1 ? 's' : ''} ignoré${duplicateCount > 1 ? 's' : ''}`
          : undefined,
      });
      onUploadComplete?.();
    } else if (duplicateCount > 0 && errorCount === 0) {
      toast.warning(`${duplicateCount} document${duplicateCount > 1 ? 's' : ''} déjà présent${duplicateCount > 1 ? 's' : ''}`, {
        description: "Ces fichiers existent déjà dans votre bibliothèque",
      });
    } else if (errorCount > 0) {
      toast.error(`Échec de l'import`, {
        description: `${errorCount} fichier${errorCount > 1 ? 's' : ''} non importé${errorCount > 1 ? 's' : ''}`,
      });
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      setFiles([]);
      setActiveTab("files");
      onOpenChange(false);
    }
  };

  const pendingCount = files.filter((f) => f.status === "pending").length;
  const processingCount = files.filter((f) => ["uploading", "validating", "processing"].includes(f.status)).length;
  const successCount = files.filter((f) => f.status === "success").length;
  const duplicateCount = files.filter((f) => f.status === "duplicate").length;
  const errorCount = files.filter((f) => f.status === "error").length;

  const getStatusIcon = (status: UploadStatus) => {
    switch (status) {
      case "uploading":
        return <Loader2 className="w-5 h-5 text-primary animate-spin" />;
      case "validating":
        return <Sparkles className="w-5 h-5 text-primary animate-pulse" />;
      case "processing":
        return <ScanLine className="w-5 h-5 text-primary animate-pulse" />;
      case "success":
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case "error":
        return <AlertCircle className="w-5 h-5 text-destructive" />;
      case "duplicate":
        return <Copy className="w-5 h-5 text-amber-500" />;
      default:
        return <FileText className="w-5 h-5 text-primary" />;
    }
  };

  const getStatusText = (status: UploadStatus) => {
    switch (status) {
      case "uploading":
        return "Téléchargement...";
      case "validating":
        return "Validation...";
      case "processing":
        return "Traitement IA...";
      case "success":
        return "Importé";
      case "error":
        return "Erreur";
      case "duplicate":
        return "Doublon";
      default:
        return "En attente";
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Importer des documents</DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full h-10 bg-muted/50">
            <TabsTrigger value="files" className="flex-1 gap-2 data-[state=active]:bg-background">
              <Upload className="w-4 h-4" />
              Fichiers
            </TabsTrigger>
            <TabsTrigger value="sources" className="flex-1 gap-2 data-[state=active]:bg-background">
              <Cloud className="w-4 h-4" />
              Sources
            </TabsTrigger>
          </TabsList>

          <AnimatePresence mode="wait">
          <TabsContent value="files" className="mt-4 space-y-4" asChild forceMount>
            <motion.div
              key="files"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              style={{ display: activeTab === "files" ? "block" : "none" }}
            >
            {/* Dropzone - always visible when not uploading */}
            {!isUploading && (
              <div
                {...getRootProps()}
                className={cn(
                  "border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all",
                  isDragActive
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/50 hover:bg-muted/30"
                )}
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <Upload className="w-7 h-7 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">
                      {isDragActive ? "Déposez vos fichiers ici" : "Glissez-déposez vos fichiers"}
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      ou cliquez pour sélectionner (PDF, images, Word - max 10 Mo)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* File list */}
            {files.length > 0 && (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {files.map((uploadFile) => (
                  <div
                    key={uploadFile.id}
                    className={cn(
                      "flex items-start gap-3 p-3 rounded-lg transition-all",
                      uploadFile.status === "success" 
                        ? "bg-green-500/5 border border-green-500/20" 
                        : uploadFile.status === "duplicate"
                        ? "bg-amber-500/5 border border-amber-500/20"
                        : uploadFile.status === "error"
                        ? "bg-destructive/5 border border-destructive/20"
                        : "bg-muted/30"
                    )}
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                      {getStatusIcon(uploadFile.status)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium truncate">
                          {uploadFile.file.name}
                        </p>
                        {uploadFile.detectedType && uploadFile.status === "success" && (
                          <Badge variant="secondary" className="text-xs shrink-0">
                            {typeLabels[uploadFile.detectedType] || uploadFile.detectedType}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-xs text-muted-foreground">
                          {(uploadFile.file.size / 1024).toFixed(0)} Ko
                        </span>
                        <span className="text-xs text-muted-foreground">•</span>
                        <span className={cn(
                          "text-xs",
                          uploadFile.status === "success" ? "text-green-600" :
                          uploadFile.status === "duplicate" ? "text-amber-600" :
                          uploadFile.status === "error" ? "text-destructive" :
                          "text-muted-foreground"
                        )}>
                          {getStatusText(uploadFile.status)}
                        </span>
                      </div>
                      {["uploading", "validating", "processing"].includes(uploadFile.status) && (
                        <Progress value={uploadFile.progress} className="h-1 mt-2" />
                      )}
                      {uploadFile.error && (
                        <p className="text-xs text-destructive mt-1">{uploadFile.error}</p>
                      )}
                    </div>
                    {!["uploading", "validating", "processing"].includes(uploadFile.status) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeFile(uploadFile.id)}
                        className="shrink-0 h-8 w-8"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Actions */}
            {files.length > 0 && (
              <div className="flex justify-between items-center pt-2 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  {successCount > 0 && `${successCount} importé${successCount > 1 ? 's' : ''}`}
                  {successCount > 0 && duplicateCount > 0 && ", "}
                  {duplicateCount > 0 && `${duplicateCount} doublon${duplicateCount > 1 ? 's' : ''}`}
                  {(successCount > 0 || duplicateCount > 0) && errorCount > 0 && ", "}
                  {errorCount > 0 && `${errorCount} erreur${errorCount > 1 ? 's' : ''}`}
                  {successCount === 0 && duplicateCount === 0 && errorCount === 0 && `${pendingCount} fichier${pendingCount > 1 ? 's' : ''} sélectionné${pendingCount > 1 ? 's' : ''}`}
                </p>
                <div className="flex gap-2">
                  {pendingCount > 0 && (
                    <Button onClick={importFiles} disabled={isUploading}>
                      {isUploading ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Import...
                        </>
                      ) : (
                        <>
                          <Check className="w-4 h-4 mr-2" />
                          Importer ({pendingCount})
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>
            )}
            </motion.div>
          </TabsContent>

          <TabsContent value="sources" className="mt-4" asChild forceMount>
            <motion.div
              key="sources"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2, ease: "easeInOut" }}
              style={{ display: activeTab === "sources" ? "block" : "none" }}
            >
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Connectez vos comptes pour importer automatiquement vos documents.
              </p>
              
              {/* Gmail Sync Progress */}
              {gmail.syncing && gmail.syncState && (
                <GmailSyncProgress
                  syncState={gmail.syncState}
                  syncing={gmail.syncing}
                  onCancel={gmail.cancelSync}
                  onReset={gmail.resetSyncState}
                />
              )}
              
              <div className="grid gap-2">
                {SOURCE_OPTIONS.map((source) => {
                  const isGmail = source.id === "gmail";
                  const isDropbox = source.id === "dropbox";
                  
                  // Determine connection status based on provider
                  const isConnected = isGmail 
                    ? gmail.status === "connected" 
                    : isDropbox 
                    ? dropbox.isConnected 
                    : false;
                  
                  const isSyncing = isGmail 
                    ? gmail.syncing 
                    : isDropbox 
                    ? dropbox.isSyncing 
                    : false;
                  
                  const lastSyncAt = isGmail 
                    ? gmail.connection?.last_sync_at 
                    : isDropbox 
                    ? dropbox.connection?.last_sync_at 
                    : null;

                  const isConnecting = isGmail 
                    ? gmail.connecting 
                    : isDropbox 
                    ? dropbox.isLoading 
                    : false;
                  
                  const handleSync = () => {
                    if (isGmail) gmail.sync();
                    else if (isDropbox) dropbox.sync();
                  };
                  
                  return (
                    <div
                      key={source.id}
                      className={cn(
                        "flex items-center gap-3 p-3 rounded-lg border transition-all",
                        source.disabled 
                          ? "opacity-50 border-border bg-muted/20" 
                          : isConnected
                          ? "border-green-500/30 bg-green-500/5"
                          : "border-border"
                      )}
                    >
                      <div className={cn("w-10 h-10 rounded-lg flex items-center justify-center shadow-sm", source.bgColor)}>
                        <source.icon />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-sm">{source.name}</p>
                          {isConnected && (
                            <Badge variant="secondary" className="h-5 text-[10px] bg-green-500/10 text-green-600 border-0">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Connecté
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground">
                          {isConnected && lastSyncAt ? (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              Dernière sync : {formatRelativeTime(lastSyncAt)}
                            </span>
                          ) : isConnected ? (
                            "Aucune synchronisation"
                          ) : (
                            source.description
                          )}
                        </p>
                      </div>
                      
                      {/* Contextual Action Button */}
                      {!source.disabled && (
                        isConnected ? (
                          <Button
                            size="sm"
                            variant="outline"
                            className="shrink-0 gap-1.5"
                            onClick={handleSync}
                            disabled={isSyncing}
                          >
                            {isSyncing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <RefreshCw className="w-4 h-4" />
                            )}
                            {isSyncing ? "Sync..." : "Synchroniser"}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="shrink-0 gap-1.5"
                            onClick={() => handleSourceConnect(source.id)}
                            disabled={isConnecting}
                          >
                            {isConnecting ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Link className="w-4 h-4" />
                            )}
                            Connecter
                          </Button>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </motion.div>
          </TabsContent>
          </AnimatePresence>
        </Tabs>

        {/* Footer */}
        <div className="flex justify-end pt-2">
          <Button variant="outline" onClick={handleClose} disabled={isUploading || processingCount > 0}>
            {successCount > 0 && pendingCount === 0 ? "Fermer" : "Annuler"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
